/**
 * Shared due-date picker — a labelled `Popover` + `Calendar` trigger button.
 *
 * Extracted to de-duplicate the identical due-date control in `DefectBaseDialog`
 * and `EditInspectionDialog` (fallow clone-group). Purely presentational; the parent
 * owns the date state and any "remove date" affordance.
 */
import { format } from "date-fns";
import type { Locale } from "date-fns";
import { CalendarIcon } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface DueDatePickerProps {
  /** Currently selected date (undefined = none). */
  value: Date | undefined;
  /** Called with the chosen date (or undefined). */
  onChange: (date: Date | undefined) => void;
  /** date-fns locale for formatting + the calendar. */
  locale: Locale;
  /** Placeholder shown on the trigger when no date is selected. */
  placeholder: string;
}

/** Labelled Popover/Calendar trigger button used by the defect + inspection dialogs. */
export function DueDatePicker({ value, onChange, locale, placeholder }: DueDatePickerProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "w-full justify-start text-left font-normal",
            !value && "text-muted-foreground",
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {value ? format(value, "PPP", { locale }) : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={onChange}
          locale={locale}
          autoFocus
          className="p-3 pointer-events-auto"
        />
      </PopoverContent>
    </Popover>
  );
}
